import { useEffect, useRef, useState } from "react";
import { HelpCircle, Sparkles, X } from "lucide-react";
import { HELP_CONTENT } from "@/lib/helpContent";
import { HelpAudience, HelpRoleNotes } from "@/components/HelpRoleExtras";

/**
 * Inline-Hilfe-Button (kleines „?"-Icon) für eine Sektion oder ein einzelnes Feld.
 * Öffnet ein Popover mit kuratiertem Erklärtext + optionalem "Mehr fragen"-CTA,
 * der den globalen KI-Assistenten mit einer Seed-Frage anstößt.
 *
 * Verwendung:
 *   <HelpButton contextId="project.tech" />
 *   <HelpButton contextId="project.consumption" size="sm" />
 *
 * Props:
 *   - contextId: Schlüssel in HELP_CONTENT
 *   - size:      "sm" | "md" (default "md")
 *   - className: zusätzliche Klassen für den Wrapper
 */
export function HelpButton({ contextId, size = "md", className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const content = HELP_CONTENT[contextId];

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!content) return null;

  const askAi = () => {
    if (content.aiSeed) {
      window.dispatchEvent(
        new CustomEvent("novatus:ai-open", { detail: { seed: content.aiSeed } })
      );
    } else {
      window.dispatchEvent(new CustomEvent("novatus:ai-open"));
    }
    setOpen(false);
  };

  const isSm = size === "sm";

  return (
    <span ref={ref} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid={`help-btn-${contextId}`}
        className={`inline-flex items-center justify-center rounded-full transition active:scale-90 ${
          isSm
            ? "h-6 w-6 text-zinc-400 hover:text-[#00E575]"
            : "h-8 w-8 bg-zinc-800/60 text-zinc-300 hover:bg-[#00E575]/15 hover:text-[#00E575]"
        }`}
        aria-label={`Hilfe: ${content.title}`}
        title={`Hilfe zu „${content.title}"`}
      >
        <HelpCircle size={isSm ? 14 : 16} />
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute z-50 top-full right-0 mt-2 w-[320px] max-w-[90vw] max-h-[70vh] overflow-y-auto bg-[#0F1014] border border-[#00E575]/30 rounded-2xl shadow-2xl p-4 text-left"
          data-testid={`help-popover-${contextId}`}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-[#00E575]/80 font-bold">
                Hilfe
              </div>
              <h4 className="font-display text-[15px] font-bold text-zinc-100 leading-tight mt-0.5">
                {content.title}
              </h4>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="h-7 w-7 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-300"
              aria-label="Schließen"
            >
              <X size={14} />
            </button>
          </div>

          <HelpAudience content={content} />
          <p className="text-[12.5px] text-zinc-300 leading-relaxed mb-3">
            {content.summary}
          </p>
          <HelpRoleNotes content={content} />

          {content.tips && content.tips.length > 0 && (
            <ul className="space-y-1.5 mb-3">
              {content.tips.map((tip, i) => (
                <li
                  key={i}
                  className="text-[12px] text-zinc-200 leading-snug flex gap-1.5 font-medium"
                >
                  <span className="text-[#00E575] mt-0.5 flex-shrink-0">▸</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          )}

          {content.aiSeed && (
            <button
              type="button"
              onClick={askAi}
              data-testid={`help-ask-ai-${contextId}`}
              className="w-full h-9 rounded-xl bg-[#00E575] text-[#09090B] text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 hover:bg-[#00C964] active:scale-[0.97] transition"
            >
              <Sparkles size={13} /> KI fragen: „{content.aiSeed.length > 38 ? content.aiSeed.slice(0, 36) + "…" : content.aiSeed}"
            </button>
          )}
        </div>
      )}
    </span>
  );
}

export default HelpButton;
